import React, { useRef, useEffect, useCallback, useState } from "react";
import { useParams } from "react-router-dom";
import { toast } from 'react-toastify';
import { cloneDeep, Template, checkTemplate, isBlankPdf } from "@pdfme/common";
import { Designer } from "@pdfme/ui";
import {
  getFontsData,
  getBlankTemplate,
  readFile,
  handleLoadTemplate,
  generatePDF,
  downloadJsonFile,
} from "../helper";
import { getPlugins } from '../plugins';
import { NavBar, NavItem } from "components/NavBar";
import DesignerModal from "./DesignerModal";
import { MReportTemplateAPI } from "api/sysadmin/m_report_template_api";
import axios from "axios";
import {Form, Button} from "react-bootstrap";
import ReqState from "lib/reqstate";

export interface TemplateStates {
  name: string;
  storeCycle: string;
  createCycle: string;
  linkedIds: string[];
}

function DesignerPage() {
  const params = useParams();
  const [id, setId] = useState(params?.id ?? "");
  const abortControllerRef = useRef<AbortController>();
  const designerRef = useRef<HTMLDivElement | null>(null);
  const designer = useRef<Designer | null>(null);
  
  const [editingStaticSchemas, setEditingStaticSchemas] = useState(false);
  const [originalTemplate, setOriginalTemplate] = useState<Template | null>(null);
  const [templateStates, setTemplateStates] = useState({name: "", storeCycle: "", createCycle: "", linkedIds: []} as TemplateStates);
  const [isModalOpen, setIsModalOpen] = useState(false);

  const [sendingState, setSendingState] = useState(ReqState.INIT);

  const buildDesigner = useCallback(async () => {
    if (!designerRef.current) return;
    try {
      let template: Template = getBlankTemplate();

      if (id) {
        const templateInfo = await MReportTemplateAPI.get(id, abortControllerRef.current!.signal);
        
        checkTemplate(templateInfo.templateFile);
        template = templateInfo.templateFile;
        setTemplateStates({...templateInfo});
      }

      designer.current = new Designer({
        domContainer: designerRef.current,
        template,
        options: {
          font: getFontsData(),
          lang: 'ja',
          labels: {
            'signature.clear': "🗑️",
          },
          theme: {
            token: { colorPrimary: "#25c2a0" },
          },
          icons: {
            multiVariableText:
              '<svg fill="#000000" width="24px" height="24px" viewBox="0 0 24 24"><path d="M6.643,13.072,17.414,2.3a1.027,1.027,0,0,1,1.452,0L20.7,4.134a1.027,1.027,0,0,1,0,1.452L9.928,16.357,5,18ZM21,20H3a1,1,0,0,0,0,2H21a1,1,0,0,0,0-2Z"/></svg>',
          },
          maxZoom: 250,
        },
        plugins: getPlugins(),
      });
      designer.current.onSaveTemplate(() => {});

    }
    catch (error: unknown) {
      if (axios.isCancel(error)) {
        return;
      }
    }
  }, []);

  const onChangeBasePDF = (e: React.ChangeEvent<HTMLInputElement>) => {
    if (e.target.files?.[0]) {
      readFile(e.target.files[0], "dataURL").then(async (basePdf) => {
        if (designer.current) {
          const newTemplate = cloneDeep(designer.current.getTemplate());
          newTemplate.basePdf = basePdf;
          designer.current.updateTemplate(newTemplate);
        }
      });
    }
  };

  const onDownloadTemplate = () => {
    if (designer.current) {
      downloadJsonFile(designer.current.getTemplate(), "template");
    }
  };

  async function onSave(templateStates: TemplateStates) {
    const template = designer.current?.getTemplate();

    if (!template) {
      return;
    }
    // update ?
    if (id == "") {
      setSendingState(ReqState.DOING);
      const res = await MReportTemplateAPI.add(
        {
          ...templateStates,
          sid: "-",
          templateFile: template
        },
        abortControllerRef.current!.signal
      );

      // 次回上書き保存のためにIDを設定
      setId(res.S);
    } else {
      setSendingState(ReqState.DOING);
      await MReportTemplateAPI.update(
        id,
        {
          ...templateStates,
          templateFile: template
        },
        abortControllerRef.current!.signal
      );
    }

    setSendingState(ReqState.DONE);
    setTemplateStates(templateStates);
    setIsModalOpen(false);
    toast.success("Saved");
  }

  const onResetTemplate = () => {
    if (designer.current) {
      designer.current.updateTemplate(getBlankTemplate());
    }
  };

  const toggleEditingStaticSchemas = () => {
    if (!designer.current) return;

    if (!editingStaticSchemas) {
      const currentTemplate = cloneDeep(designer.current.getTemplate());
      if (!isBlankPdf(currentTemplate.basePdf)) {
        toast.error(<div>
          <p>The current template cannot edit the static schema.</p>
        </div>);
        return;
      }

      setOriginalTemplate(currentTemplate);

      const { width, height } = currentTemplate.basePdf;
      const staticSchema = currentTemplate.basePdf.staticSchema || [];
      designer.current.updateTemplate({
        ...currentTemplate,
        schemas: [staticSchema],
        basePdf: { width, height, padding: [0, 0, 0, 0] },
      });

      setEditingStaticSchemas(true);

    } else {
      const editedTemplate = designer.current.getTemplate();
      if (!originalTemplate) return;
      const merged = cloneDeep(originalTemplate);
      if (!isBlankPdf(merged.basePdf)) {
        toast.error("Invalid basePdf format");
        return;
      }

      merged.basePdf.staticSchema = editedTemplate.schemas[0];
      designer.current.updateTemplate(merged);

      setOriginalTemplate(null);
      setEditingStaticSchemas(false);
    }
  };

  useEffect(() => {
    abortControllerRef.current = new AbortController();
    if (designerRef.current) {
      buildDesigner();
    }

    return () => {
      abortControllerRef.current!.abort();
      designer.current?.destroy();
    };
  }, [designerRef, buildDesigner]);

  const navItems: NavItem[] = [
    {
      label: "ベースPDFを設定",
      content: (
        <Form.Control
          size="sm"
          disabled={editingStaticSchemas}
          type="file"
          accept="application/pdf"
          onChange={onChangeBasePDF}
        />
      ),
    },
    {
      label: "テンプレートを設定",
      content: (
        <Form.Control
          size="sm"
          disabled={editingStaticSchemas}
          type="file"
          accept="application/json"
          onChange={(e: React.ChangeEvent<HTMLInputElement>) => handleLoadTemplate(e, designer.current)}
        />
      ),
    },
    {
      label: "ベースを編集",
      content: (
        <div>
          <Button
            variant="light"
            className="border border-secondary"
            size="sm"
            onClick={toggleEditingStaticSchemas}
          >
            {editingStaticSchemas ? "End editing" : "Start editing"}
          </Button>
        </div>
      ),
    },
    {
      label: "",
      content: (
        <div>
          <Button
            id="save"
            variant="light"
            size="sm"
            className="me-2 border border-secondary"
            disabled={editingStaticSchemas}
            onClick={() => setIsModalOpen(true)}
          >
            保存
          </Button>
          <Button
            id="reset-template"
            variant="light"
            size="sm"
            className="me-2 border border-secondary"
            disabled={editingStaticSchemas}
            onClick={onResetTemplate}
          >
            リセット
          </Button>
        </div>
      ),
    },
    {
      label: "",
      content: (
        <div>
          <Button
            variant="light"  
            size="sm"
            className="me-2 border border-secondary"
            disabled={editingStaticSchemas}
            onClick={onDownloadTemplate}
          >
            エクスポート
          </Button>
          <Button
            id="generate-pdf"
            variant="light"
            size="sm"
            className="me-2 border border-secondary"
            disabled={editingStaticSchemas}
            onClick={async () => {
              const startTimer = performance.now();
              await generatePDF(designer.current);
              const endTimer = performance.now();
              toast.info(`Generated PDF in ${Math.round(endTimer - startTimer)}ms ⚡️`);
            }}
          >
            PDF出力
          </Button>
        </div>
      ),
    },
  ];

  return (
    <>
      <NavBar items={navItems} />
      {isModalOpen &&
        <DesignerModal initialValues={templateStates} sendingState={sendingState} onSave={onSave} onClose={() => setIsModalOpen(false)} />
      }
      <div ref={designerRef} className="flex-grow-1 flex-shrink-1" />
    </>
  );
}

export default DesignerPage;
